import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, Table } from '@material-ui/core';
import { BoxWrapper } from 'components/Base';
import TableComponents from 'components/Table';
import { FundType } from 'store/modules/Funds/types';
import CipFund from './CipFund';
import InfoBanner from 'components/Base/InfoBanner/InfoBanner';
import Store from 'store/store';
import api from 'api';
import Router from 'components/Router/Router';
import CircularProgress from '@material-ui/core/CircularProgress';

const { connect, selectors, actions } = Store;
const { useParams } = Router;

const useStyles = makeStyles({
  cipFunds: {
    marginTop: '1rem'
  },
  underline: {
    borderBottom: '4px solid #eaeaea',
    height: '0px',
    padding: '0px'
  }
});

interface CipTableProps {
  cipTemplate: Array<any>;
  toggleCipFund: (fund: any) => void;
  fundsAdded: Array<FundType>;
  addModelPortfolio: any;
  firmRiskGroup: number;
  riskSummaries: any;
  setRiskSummaries: any;
  timeHorizons: any;
  isEvalue: boolean;
}

interface FundDetailed {
  alloction: string | null;
  fund: FundType;
  fund_value: string | null;
}

const CipTable = ({
  cipTemplate,
  toggleCipFund,
  fundsAdded,
  firmRiskGroup,
  riskSummaries,
  setRiskSummaries,
  timeHorizons,
  isEvalue
}: CipTableProps) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const shouldRenderUnderline = (funds: any, index: number) => {
    if (index === funds.length - 1 || funds.length === 1) return false;
    if (
      funds[index + 1].cip_threshold.display_name !==
      funds[index].cip_threshold.display_name
    )
      return true;
  };

  const { portfolio } = useParams() as any;

  const [fundFields, setFundFields] = useState(cipTemplate);

  useEffect(() => {
    setFundFields(cipTemplate);
  }, [cipTemplate]);

  const fetchedRiskRefs = useRef<{ [key: string]: boolean }>({});
  const latestFundFieldsRef = useRef(fundFields);

  // Keep latest fundFields in ref
  useEffect(() => {
    latestFundFieldsRef.current = fundFields;
  }, [fundFields]);

  useEffect(() => {
    fundFields.forEach((fund: any, index: number) => {
      const id = fund.model_portfolio?.id || fund.fund?.id || `index-${index}`;

      if (
        (isEvalue && !fund.fund) ||
        fund.risk !== undefined ||
        fund.gettingRisk ||
        fetchedRiskRefs.current[id]
      ) {
        return;
      }

      fetchedRiskRefs.current[id] = true;

      // Wrap in an IIFE to handle async inside loop
      (async () => {
        const currentFields = [...latestFundFieldsRef.current];
        currentFields[index] = { ...fund, gettingRisk: true };
        setFundFields(currentFields);
        latestFundFieldsRef.current = currentFields;

        const method = isEvalue
          ? api.getFundRiskRating({ fundId: fund.fund.id, caseId: portfolio })
          : api.getMorningstarRiskRating({
              fundId: fund.fund?.id,
              modelPortfolioId: fund.model_portfolio?.id
            });

        try {
          const { data } = await method;
          const risk = data === 0 ? '-' : data;

          const updatedFields = [...latestFundFieldsRef.current];
          updatedFields[index] = { ...fund, risk, gettingRisk: false };
          setFundFields(updatedFields);
          latestFundFieldsRef.current = updatedFields;
        } catch {
          const updatedFields = [...latestFundFieldsRef.current];
          updatedFields[index] = { ...fund, risk: '-', gettingRisk: false };
          setFundFields(updatedFields);
          latestFundFieldsRef.current = updatedFields;
        }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundFields]);

  useEffect(() => {
    setIsLoading(true);
    api.getRiskSummaries({ portfolio }).then(response => {
      setRiskSummaries(response.data);
      setIsLoading(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTimeHorizon = (targetValue: number) => {
    return timeHorizons.find(
      (summary: { value: any }) => Number(summary.value) === targetValue
    )?.name;
  };

  const title = isLoading
    ? ''
    : isEvalue
      ? `CIP selection for risk profile ${
          riskSummaries[0].recommended_risk_profile
        }/${firmRiskGroup}, over a ${getTimeHorizon(
          riskSummaries[0].time_horizon_interval
        )} year time horizon`
      : `CIP selection for risk tolerance: ${riskSummaries[0].recommended_risk_profile}`;

  const riskHeaders = isEvalue
    ? ['Risk']
    : ['Growth assets', 'Risk comfort score'];

  return !isLoading ? (
    <BoxWrapper title={title} className={classes.cipFunds}>
      <Table style={{ backgroundColor: '#fff' }}>
        {!!fundFields.length && (
          <TableComponents.TableHead
            cells={[
              '',
              'Investment option',
              'Fund',
              'Sector',
              ...riskHeaders,
              'Existing weighting %',
              'Recommended weighting %',
              ''
            ]}
          />
        )}
        <TableComponents.TableBody>
          {fundFields.length ? (
            fundFields.map((fundDetails: any, index: number) => {
              const isModelPortfolio = !!fundDetails.model_portfolio;

              const isAlreadyAdded = fundsAdded.some((addedFund: any) => {
                const isModelPortfolioAddedFund = !!addedFund.model_portfolio;

                const idToCheckExisting = isModelPortfolioAddedFund
                  ? addedFund.model_portfolio.id
                  : addedFund.fund.id;

                const idToCheckFund = isModelPortfolio
                  ? fundDetails.model_portfolio.id
                  : fundDetails.fund.id;

                return idToCheckExisting === idToCheckFund;
              });

              const key =
                fundDetails.model_portfolio?.id ||
                fundDetails.fund?.id ||
                index;

              return (
                <React.Fragment key={key}>
                  <CipFund
                    fundDetails={fundDetails}
                    isFundAdded={isAlreadyAdded}
                    toggleCipFund={toggleCipFund}
                    isEvalue={isEvalue}
                    risk={
                      fundDetails.risk !== undefined ? (
                        fundDetails.risk
                      ) : (
                        <CircularProgress
                          style={{ flex: 'none' }}
                          color="inherit"
                          size={12}
                        />
                      )
                    }
                  />
                  {shouldRenderUnderline(fundFields, index) && (
                    <>
                      <>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                      </>
                      <>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                        <TableComponents.TableCell
                          className={classes.underline}
                        ></TableComponents.TableCell>
                      </>
                    </>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <InfoBanner
              text={`Based on the CIP selection, there are no available solutions that meet the recommended ${!isEvalue ? 'time-weighted ' : ''}risk profile`}
            />
          )}
        </TableComponents.TableBody>
      </Table>
    </BoxWrapper>
  ) : null;
};

export default connect(
  (state: any) => ({
    firmRiskGroup: selectors.firmSelectors.riskGroup(state),
    riskSummaries: selectors.caseSelectors.getRiskSummary(state),
    timeHorizons: selectors.metaSelectors.timeHorizons(state)
  }),
  {
    setRiskSummaries: actions.caseActions.setRiskSummaries
  }
)(CipTable);
